/*
  Emily Harrison London: predictive search (chrome builder).
  <predictive-search> queries Shopify's /search/suggest endpoint (locale aware route) and
  renders sections/predictive-search.liquid. States: searching (shown while a request
  runs), results, empty ("No results for ..."), error ("Search is temporarily unavailable").
  Requests are debounced (250 ms), cancelled when the query changes and cached per query.
  Keyboard: Arrow Up/Down move through the options (aria-activedescendant), Enter opens the
  selected option, Escape clears the results first and then closes the panel.
*/
(() => {
  if (customElements.get('predictive-search')) return;

  const EHL = window.EHL || {};
  const strings = EHL.strings || {};
  const suggestUrl = () => {
    const route = (EHL.routes && EHL.routes.predictiveSearch) || '/search/suggest';
    return new URL(route, window.location.origin);
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.input = this.querySelector('[data-predictive-search-input]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.status = this.querySelector('[data-predictive-search-status]');
      this.form = this.querySelector('form');
      this.details = this.closest('[data-header-search]');
      this.sectionId = this.dataset.sectionId || 'predictive-search';
      this.cache = new Map();
      this.controller = null;
      this.activeIndex = -1;
      this.timer = null;
      if (!this.input || !this.results) return;

      this.input.addEventListener('input', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => this.onChange(), 250);
      });
      this.input.addEventListener('focus', () => {
        const term = this.term();
        if (term && this.cache.has(term)) this.render(this.cache.get(term), term);
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      if (this.form) {
        this.form.addEventListener('submit', (event) => {
          if (!this.term()) {
            event.preventDefault();
            this.input.focus();
          }
        });
      }
      if (this.details) {
        this.details.addEventListener('toggle', () => {
          if (!this.details.open) this.close();
        });
      }
    }

    term() {
      return (this.input.value || '').trim();
    }

    onChange() {
      const term = this.term();
      if (!term) {
        this.close();
        return;
      }
      if (this.cache.has(term)) {
        this.render(this.cache.get(term), term);
        return;
      }
      this.renderStatus(strings.searching || 'Searching...', true);
      this.fetchResults(term);
    }

    fetchResults(term) {
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const url = suggestUrl();
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.set('resources[type]', 'product,collection,page');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      fetch(url.toString(), { signal: this.controller.signal, headers: { Accept: 'text/html' } })
        .then((response) => {
          if (!response.ok) throw new Error(`Predictive search failed with status ${response.status}`);
          return response.text();
        })
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const section = doc.querySelector(`#shopify-section-${this.sectionId}`);
          const markup = section ? section.innerHTML.trim() : '';
          if (!markup) throw new Error('Predictive search returned no markup');
          this.cache.set(term, markup);
          if (this.term() === term) this.render(markup, term);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          if (this.term() === term) this.renderStatus(strings.searchUnavailable || 'Search is temporarily unavailable.');
        });
    }

    render(markup, term) {
      if (this.term() !== term) return;
      this.results.innerHTML = markup;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      const listbox = this.results.querySelector('[role="listbox"]');
      if (listbox) this.input.setAttribute('aria-controls', listbox.id);
      else this.input.setAttribute('aria-controls', this.results.id);
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      const summary = this.results.querySelector('[data-predictive-search-status-text]');
      this.announce(summary ? summary.dataset.predictiveSearchStatusText : '');
    }

    renderStatus(message, loading = false) {
      this.results.hidden = false;
      this.results.innerHTML = `
        <div class="predictive-search-results predictive-search-results--status" ${loading ? 'aria-busy="true"' : ''}>
          <p>${loading ? '<span class="loading-spinner" aria-hidden="true"></span>' : ''}${escapeHtml(message)}</p>
        </div>`;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.announce(message);
    }

    announce(message) {
      if (this.status) this.status.textContent = message || '';
    }

    close() {
      if (this.controller) this.controller.abort();
      window.clearTimeout(this.timer);
      this.results.hidden = true;
      this.results.innerHTML = '';
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.announce('');
    }

    options() {
      return [...this.results.querySelectorAll('[data-predictive-search-option]')];
    }

    select(index) {
      const options = this.options();
      if (!options.length) return;
      const next = (index + options.length) % options.length;
      options.forEach((option, i) => option.setAttribute('aria-selected', i === next ? 'true' : 'false'));
      this.activeIndex = next;
      const active = options[next];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      const resultsOpen = !this.results.hidden && this.results.innerHTML !== '';
      switch (event.key) {
        case 'ArrowDown':
          if (!resultsOpen) return;
          event.preventDefault();
          this.select(this.activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!resultsOpen) return;
          event.preventDefault();
          this.select(this.activeIndex - 1);
          break;
        case 'Enter': {
          if (this.activeIndex < 0) return;
          const option = this.options()[this.activeIndex];
          const link = option && (option.matches('a') ? option : option.querySelector('a'));
          if (link) {
            event.preventDefault();
            window.location.href = link.href;
          }
          break;
        }
        case 'Escape':
          if (resultsOpen) {
            event.preventDefault();
            this.close();
          }
          break;
        default:
          break;
      }
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
